// components/TokenCard.js
import React from 'react';
import { useNavigate } from 'react-router-dom';

const TokenCard = ({ token }) => {
    const formatNumber = (num) => Number(num).toFixed(15);
    const formatPercentage = (num) => Number(num).toFixed(2);
    const navigate = useNavigate();


    // Before navigating, encode each parameter properly
    const queryParams = new URLSearchParams({
        quoteMint: token.quoteMintAddress,
        callerName: token.callerName,
        initialPrice: token.initialPrice,
        currentPrice: token.currentPrice,
        highestPrice: token.highestPriceReached,
        stopLoss: token.stopLossPrice,
        remaining: token.remainingPosition,
        profitLoss: token.profitLossPercentage,
        levels: JSON.stringify(token.strategyState.levelsReached)
    });

    return (
        <div style={styles.card} >
            <h2 style={styles.title}>
                <span>🔹</span>
                {token.callerName}
                <span>|</span>
                <span style={styles.button} onClick={() =>
                    navigate(`/position?${queryParams.toString()}`)
                }>View</span>
            </h2>

            <div style={styles.address}>
                {token.quoteMintAddress}
            </div>

            <div style={styles.dataGrid}>
                <div style={styles.label}>Initial Price:</div>
                <div style={styles.value}>{formatNumber(token.initialPrice)} SOL</div>

                <div style={styles.label}>Current Price:</div>
                <div style={styles.value}>{formatNumber(token.currentPrice)} SOL</div>

                <div style={styles.label}>Highest Price:</div>
                <div style={styles.value}>{formatNumber(token.highestPriceReached)} SOL</div>

                <div style={styles.label}>Stop Loss:</div>
                <div style={styles.value}>{formatNumber(token.stopLossPrice)} SOL</div>

                <div style={styles.label}>Profit/Loss:</div>
                <div style={token.profitLossPercentage >= 0 ? styles.profitPositive : styles.profitNegative}>
                    {formatPercentage(token.profitLossPercentage)}%
                </div>

                <div style={styles.label}>Remaining Position:</div>
                <div style={styles.value}>{token.remainingPosition}</div>

                <div style={styles.label}>Consecutive Profits:</div>
                <div style={styles.value}>
                    {token.strategyState.consecutiveProfitsTaken || "None"}
                </div>
            </div>

            <div style={styles.levelsContainer}>
                <div style={styles.label}>Levels Reached:</div>
                <div style={styles.levelGrid}>
                    {Object.entries(token.strategyState.levelsReached).map(([level, reached]) => (
                        <div key={level} style={styles.levelItem}>
                            <span>Level {level}</span>
                            <span>{reached ? '✅' : '⭕'}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div style={styles.timestamp}>
                Opened: {new Date(token.openTime).toLocaleString()}
            </div>
        </div>
    );
};

const styles = {
    container: {
        padding: '20px',
        maxWidth: '1200px',
        margin: '0 auto',
    },
    header: {
        textAlign: 'center',
        marginBottom: '20px',
        borderBottom: '2px solid #eaeaea',
        paddingBottom: '15px',
    },
    headerTitle: {
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#333',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
    },
    lastUpdated: {
        color: '#666',
        fontSize: '14px',
        marginTop: '5px',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '20px',
        padding: '10px',
    },
    card: {
        cursor: 'pointer',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        border: '1px solid #eaeaea',
        transition: 'transform 0.2s ease',
        ':hover': {
            transform: 'translateY(-2px)',
        },
    },
    title: {
        color: '#2563eb',
        fontSize: '18px',
        fontWeight: '600',
        marginBottom: '15px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    address: {
        backgroundColor: '#f8f9fa',
        padding: '8px',
        borderRadius: '6px',
        fontSize: '12px',
        wordBreak: 'break-all',
        marginBottom: '15px',
        color: '#666',
    },
    dataGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '10px',
        marginBottom: '15px',
    },
    label: {
        color: '#666',
        fontSize: '13px',
        fontWeight: '500',
    },
    value: {
        color: '#333',
        fontSize: '13px',
        fontWeight: '600',
        textAlign: 'right',
    },
    profitPositive: {
        color: '#10b981',
        fontWeight: '600',
    },
    profitNegative: {
        color: '#ef4444',
        fontWeight: '600',
    },
    levelsContainer: {
        marginTop: '15px',
        borderTop: '1px solid #eaeaea',
        paddingTop: '15px',
    },
    levelGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '8px',
        marginTop: '8px',
    },
    levelItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '4px 8px',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        fontSize: '12px',
    },
    timestamp: {
        color: '#666',
        fontSize: '12px',
        marginTop: '15px',
        textAlign: 'right',
        fontStyle: 'italic',
    },
    button: {
        backgroundColor: '#3B82F6', // bg-blue-500
        color: '#FFFFFF',
        padding: '0.5rem 1rem', // px-4 py-2
        borderRadius: '0.375rem', // rounded-md
        fontSize: '0.875rem', // text-sm
        fontWeight: '500', // font-medium
        cursor: 'pointer',
        border: 'none',
        ':hover': {
            backgroundColor: '#2563EB', // hover:bg-blue-600
        },
    }
};

export default TokenCard;